import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { PlusMinusInputOptions } from './plus-minus-input';
import { distinctUntilChanged } from 'rxjs/operators';
import { CurrencyDigitShortcutPipeInput } from 'src/app/pipes/pipes';
import { defaultRiskLevels } from '../constants';

export const digitsRegex = /[^0-9\s]/g;
export const thousandsSeparatingRegex = /\B(?=(\d{3})+(?!\d))/g;

@Component({
  selector: 'libero-plus-minus-input',
  templateUrl: './plus-minus-input.component.html',
  styleUrls: ['./plus-minus-input.component.scss'],
  standalone: false
})
export class PlusMinusInputComponent implements OnInit, OnDestroy {
  @Input() public options!: PlusMinusInputOptions;
  @Input() public formName!: AbstractControl;
  @Input() public digitShortcutsOptions?: CurrencyDigitShortcutPipeInput;
  @Input() public invalidClass = false;
  @Input() public disabled = false;
  @Input() public autoSetMinIfLower = false;
  @Input() public minusBtnTestId = '';
  @Input() public efektikaMode = false;
  @Input() public efektikaEditingMode = false;

  public currentValue!: number;
  public currentInputValue!: string;

  public currentValueDebouncer = new Subject<string>();

  public currentSliderValue!: number;

  public riskLevels = defaultRiskLevels;

  public constructor(
    public elementRef: ElementRef<HTMLElement>,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.currentValueDebouncer.pipe(
      distinctUntilChanged()
    ).subscribe(value => {
        const valueWithoutFormatting = parseInt(value.replaceAll(' ', ''));

        this.formName.patchValue(valueWithoutFormatting);
        this.formName.markAsTouched();
        this.formName.markAsDirty();
      }
    );
  }

  public ngOnInit(): void {
    this.validateInitialValue();
  }

  private validateInitialValue(): void {
    this.currentValue = 0;

    if (this.formName?.value) {
      this.currentValue = this.formName.value;
    }

    if(this.options.minValue > this.currentValue && !this.options.allowZero) {
      this.updateValue(this.options.minValue);
    } else if (this.options.maxValue <=  this.currentValue) {
      this.updateValue(this.options.maxValue);
    } else {
      this.updateValue(this.currentValue);
    }
  }

  public clickedPlus(): void {
    const newValue = this.currentValue + this.options.step;

     if (this.valueInLimits(newValue)) {
      this.updateValue(newValue);
    } else {
      this.updateToClosest(newValue);
    }
  }

  public clickedMinus(): void {
    const newValue = this.currentValue - this.options.step;

    if (newValue < this.options.minValue && this.autoSetMinIfLower && !this.options.allowZero) {
      this.updateValue(this.options.minValue);
    } else if(newValue < this.options.minValue && this.options.allowZero) {
      this.updateValue(0);
    } else if (this.valueInLimits(newValue)) {
      this.updateValue(newValue);
    } else {
      this.updateToClosest(newValue);
    }
  }

  private updateToClosest(newValue: number): void {
    if (newValue > this.options.maxValue) {
      this.updateValue(this.options.maxValue);
    } else {
      this.updateValue(this.options.minValue);
    }
  }

  public updateValue(newValue: number): void {
    this.currentInputValue = this.transformForView(newValue);

    this.currentValue = newValue;
    this.currentValueDebouncer.next(newValue.toString());
  }

  private transformForView(newValue: number): string {
    const onlyNumbersValue = newValue.toString().replace(digitsRegex, '');
    const thousandsSpaceSeparated = onlyNumbersValue.replace(thousandsSeparatingRegex, ' ');
    return thousandsSpaceSeparated;
  }

  private valueInLimits(value: number): boolean {
    return value >= this.options.minValue && value <= this.options.maxValue;
  }

  private autoResetToMinTimeout: ReturnType<typeof setTimeout> | null = null;

  public textInputUpdated(): void {
    // Clear any existing timeout (works as debounce for auto set min)
    if (this.autoResetToMinTimeout) {
      clearTimeout(this.autoResetToMinTimeout);
    }

    this.parseSliderNumToString();
    this.changeDetectorRef.detectChanges();

    const inputWithoutFormatting = this.currentInputValue.replaceAll(' ', '');
    const onlyNumbersValue = parseInt(inputWithoutFormatting.replace(digitsRegex, ''));

    if(0 >= onlyNumbersValue) {
      if(this.options.allowZero) {
        this.updateValue(0);
        this.currentInputValue = this.transformForView(0);
      } else {
        this.currentInputValue = this.transformForView(this.options.minValue);
        this.updateValue(this.options.minValue);
      }
    } else if (this.autoSetMinIfLower && this.options.minValue >= onlyNumbersValue) {
      this.autoResetToMinTimeout = setTimeout(() => {
        this.currentInputValue = this.transformForView(this.options.minValue);
        this.updateValue(this.options.minValue);
      }, 1000);
    } else if (this.options.maxValue <= onlyNumbersValue) {
      this.currentInputValue = this.transformForView(this.options.maxValue);
      this.updateValue(this.options.maxValue);
    } else {
      if(this.inputContainsLetters()) {
        // handle viewed input value
        setTimeout(() => {
          this.currentInputValue = this.transformForView(onlyNumbersValue);
          this.updateValue(onlyNumbersValue);
        }, 100);
      } else {
        this.updateValue(onlyNumbersValue);
      }
    }
  }

  private inputContainsLetters(): boolean {
    const inputWithoutFormatting = this.currentInputValue.replaceAll(' ', '');
    return digitsRegex.test(inputWithoutFormatting);
  }

  public invalidInput(): boolean {
    if (!this.currentInputValue || this.invalidClass) {
      return true;
    }

    this.parseSliderNumToString();

    const unformatted = this.currentInputValue.replaceAll(' ', '');
    const asNumber = parseInt(unformatted.replace(digitsRegex, ''));

    if(asNumber === 0 && this.options.allowZero) {
      return false;
    }

    if (asNumber < this.options.minValue) {
      return true;
    }

    if (asNumber > this.options.maxValue) {
      return true;
    }

    return this.inputContainsLetters();
  }

  private parseSliderNumToString(): void {
    if (typeof this.currentInputValue === 'number' && !isNaN(this.currentInputValue)) {
      this.currentInputValue = (this.currentInputValue as number).toString()
    }
  }

  public sliderInputUpdated(event: Event): void {
    if ((event?.target as HTMLInputElement)?.value) {
      const sliderNewValue = (event.target as HTMLInputElement).value as unknown as number;
      if (this.autoSetMinIfLower && this.options.minValue >= sliderNewValue && !this.options.allowZero) {
        this.updateValue(this.options.minValue);
      } else {
        this.updateValue(sliderNewValue);
      }
    }
  }

  public ngOnDestroy(): void {
    this.currentValueDebouncer.unsubscribe();
  }

  public get isMinusBtnDisabled(): boolean {
    return this.disabled || (this.autoSetMinIfLower && this.currentValue === this.options.minValue && !this.options.allowZero);
  }

  public get isPlusBtnDisabled(): boolean {
    return this.disabled || this.currentValue === this.options.maxValue;
  }

}
